import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue';
import { Mail, MessageSquare, NotebookTabs, Phone, ShieldCheck } from 'lucide-vue-next';
import { toast } from 'vue-sonner';
import { toMillis } from '~/lib/timestamps';
import type { Activity, Client, Message } from '~/types/models';
import type { ClientCommsTouchpointDraft } from '@/components/clients/comms/types';

type CommunicationChannel = 'app' | 'email' | 'whatsapp' | 'call' | 'note';

export function useClientWorkspaceCommunications(options: {
    client: MaybeRefOrGetter<Client | null>;
    activityStore: any;
    messagesStore: any;
    userStore: any;
    updateLifecycle: (patch: { lastEngagedAt: Date }) => Promise<void>;
    refreshClient: () => Promise<void>;
    formatRelativeTime: (value: unknown, fallback?: string) => string;
}) {
    const clientActivities = ref<Activity[]>([]);
    const loading = ref(false);
    const error = ref('');
    const saving = ref(false);
    const draft = ref<ClientCommsTouchpointDraft>({
        channel: 'note',
        direction: 'outbound',
        subject: '',
        body: '',
        occurredAt: '',
    });

    const clientId = computed(() => String(toValue(options.client)?.id || '').trim());
    const threadSummary = computed(() => (
        options.messagesStore.threadSummaries.find((thread: any) => thread.clientId === clientId.value) || null
    ));
    const clientMessages = computed<Message[]>(() => (
        clientId.value ? options.messagesStore.messagesByThread[`client:${clientId.value}`] || [] : []
    ));
    const clientTouchpoints = computed(() => clientActivities.value
        .filter(activity => activity.action === 'comment' && activity.changedField === 'touchpoint'));

    const timeline = computed(() => {
        const touchpoints = clientTouchpoints.value.map((activity) => {
            const payload = typeof activity.newValue === 'object' && activity.newValue !== null
                ? activity.newValue as Record<string, any>
                : {};
            const channel = String(payload.channel || 'note').trim() as Exclude<CommunicationChannel, 'app'>;
            const occurredAt = payload.occurredAt || activity.createdAt;
            return {
                id: `touchpoint:${activity.id}`,
                type: 'touchpoint' as const,
                channel,
                title: String(payload.subject || activity.description || `Logged ${channel}`),
                body: String(payload.body || ''),
                direction: String(payload.direction || 'outbound') as 'outbound' | 'inbound',
                actorName: activity.userName || 'Team member',
                at: occurredAt,
                relativeAt: options.formatRelativeTime(occurredAt, 'recently'),
            };
        });
        const messages = clientMessages.value.map(message => ({
            id: `message:${message.id}`,
            type: 'message' as const,
            channel: (message.channel || 'app') as 'app' | 'email' | 'whatsapp',
            title: message.subject || message.content.slice(0, 96) || 'Message update',
            body: message.content,
            direction: message.direction === 'tenant_to_client' ? 'outbound' as const : 'inbound' as const,
            actorName: message.senderName || 'Unknown sender',
            at: message.createdAt,
            relativeAt: options.formatRelativeTime(message.createdAt, 'recently'),
        }));
        return [...touchpoints, ...messages].sort((left, right) => toMillis(right.at) - toMillis(left.at));
    });

    function channelLabel(channel: CommunicationChannel): string {
        return { email: 'Email', whatsapp: 'WhatsApp', call: 'Call', note: 'Note', app: 'Message' }[channel];
    }

    function directionLabel(direction: 'inbound' | 'outbound'): string {
        return direction === 'inbound' ? 'Inbound' : 'Outbound';
    }

    function channelIcon(channel: CommunicationChannel) {
        return { email: Mail, whatsapp: MessageSquare, call: Phone, note: NotebookTabs, app: ShieldCheck }[channel];
    }

    function channelBadgeClass(channel: CommunicationChannel): string {
        return {
            email: 'border-violet-400/35 bg-violet-500/12 text-violet-700 dark:text-violet-100',
            whatsapp: 'border-emerald-400/35 bg-emerald-500/12 text-emerald-700 dark:text-emerald-100',
            call: 'border-sky-400/35 bg-sky-500/12 text-sky-700 dark:text-sky-100',
            note: 'border-amber-400/35 bg-amber-500/12 text-amber-700 dark:text-amber-100',
            app: 'border-blue-400/35 bg-blue-500/12 text-blue-700 dark:text-blue-100',
        }[channel];
    }

    function resetDraft() {
        draft.value = { channel: 'note', direction: 'outbound', subject: '', body: '', occurredAt: '' };
    }

    async function load() {
        if (!clientId.value) {
            clientActivities.value = [];
            error.value = '';
            return;
        }
        loading.value = true;
        error.value = '';
        try {
            const [activities, messages] = await Promise.all([
                options.activityStore.fetchActivitiesByEntity('client', clientId.value, 100),
                options.messagesStore.fetchThread(clientId.value),
            ]);
            clientActivities.value = [...activities].sort((left, right) => toMillis(right.createdAt) - toMillis(left.createdAt));
            options.messagesStore.messagesByThread[`client:${clientId.value}`] = messages;
        } catch (loadError: any) {
            console.error('Failed to load client communications:', loadError);
            error.value = loadError?.message || 'Could not load client communications.';
            clientActivities.value = [];
        } finally {
            loading.value = false;
        }
    }

    async function log(input?: ClientCommsTouchpointDraft) {
        if (!clientId.value) return;
        const next = input || draft.value;
        const body = next.body.trim();
        const subject = next.subject.trim();
        if (!body) {
            toast.error('Add a short note so the communication touchpoint has some context.');
            return;
        }
        saving.value = true;
        try {
            const selectedDate = next.occurredAt ? new Date(next.occurredAt) : new Date();
            const occurredAt = Number.isNaN(selectedDate.getTime()) ? new Date() : selectedDate;
            await options.activityStore.logActivity({
                action: 'comment',
                entityType: 'client',
                entityId: clientId.value,
                description: subject || `Logged ${channelLabel(next.channel).toLowerCase()} touchpoint`,
                userId: options.userStore.user?.id || options.userStore.userId || 'unknown',
                userName: options.userStore.user?.displayName || options.userStore.user?.fullName,
                userAvatar: options.userStore.user?.avatarURL,
                changedField: 'touchpoint',
                newValue: { channel: next.channel, direction: next.direction, subject, body, occurredAt },
            });
            await options.updateLifecycle({ lastEngagedAt: occurredAt });
            await Promise.all([options.refreshClient(), load()]);
            resetDraft();
            toast.success('Communication touchpoint logged.');
        } catch (saveError: any) {
            toast.error(saveError?.message || 'Could not log the communication touchpoint.');
        } finally {
            saving.value = false;
        }
    }

    return {
        clientActivities,
        clientTouchpoints,
        threadSummary,
        timeline,
        loading,
        error,
        saving,
        draft,
        load,
        log,
        channelLabel,
        directionLabel,
        channelIcon,
        channelBadgeClass,
    };
}
